import type { TFunction } from "i18next";
import { formatCalendarDate, type ClientRecord } from "./client";

/**
 * Narrows a broadcast to part of the alumni — a program's alumni, say, or one discharge cohort.
 * Every field is optional; one left unset matches everyone. A filter that is set leaves out anyone
 * with nothing on file for it. Several locations match anyone at any one of them.
 *
 * Location and sex are free text from KIPU, so they match whatever the source wrote, ignoring case
 * and surrounding spaces: "Female" and "female " are one choice, not two that each miss some people.
 */
export interface AudienceFilter {
    locations?: string[];
    sex?: string;
    /** `YYYY-MM-DD`, inclusive. */
    dischargedFrom?: string;
    /** `YYYY-MM-DD`, inclusive. */
    dischargedTo?: string;
}

const sameText = (value: string) => value.trim().toLocaleLowerCase();

const matchesText = (wanted: string | undefined, value: string | undefined): boolean =>
    !wanted || (value !== undefined && sameText(value) === sameText(wanted));

export const isFiltered = (filter: AudienceFilter): boolean =>
    Boolean(filter.locations?.length || filter.sex || filter.dischargedFrom || filter.dischargedTo);

export const matchesAudienceFilter = (client: ClientRecord, filter: AudienceFilter): boolean => {
    if (filter.locations?.length && !filter.locations.some((location) => matchesText(location, client.location))) {
        return false;
    }
    if (!matchesText(filter.sex, client.sex)) return false;
    if (filter.dischargedFrom || filter.dischargedTo) {
        // Calendar dates as YYYY-MM-DD compare in order as strings.
        const discharged = client.dischargeDate;
        if (!discharged) return false;
        if (filter.dischargedFrom && discharged < filter.dischargedFrom) return false;
        if (filter.dischargedTo && discharged > filter.dischargedTo) return false;
    }
    return true;
};

/** The distinct values on file, one per spelling that differs only in case or spaces, sorted. */
export const filterChoices = (values: (string | undefined)[]): string[] => {
    const choices = new Map<string, string>();
    for (const value of values) {
        if (value && !choices.has(sameText(value))) choices.set(sameText(value), value.trim());
    }
    return [...choices.values()].sort((a, b) => a.localeCompare(b));
};

/**
 * The filters that are set, in words, for a confirmation: "location Westchester or Boston, discharged
 * Jan 1 to Mar 1".
 */
export const audienceFilterSummary = (filter: AudienceFilter, t: TFunction): string =>
    [
        filter.locations?.length
            ? t("admin.alumni.broadcast.filters.summaryLocation", {
                  location: new Intl.ListFormat(undefined, { type: "disjunction" }).format(filter.locations),
              })
            : null,
        filter.sex ? t("admin.alumni.broadcast.filters.summarySex", { sex: filter.sex }) : null,
        // The picker sets both ends; one day picked is a range from that day to itself.
        filter.dischargedFrom && filter.dischargedTo
            ? filter.dischargedFrom === filter.dischargedTo
                ? t("admin.alumni.broadcast.filters.summaryDischargedOn", {
                      date: formatCalendarDate(filter.dischargedFrom),
                  })
                : t("admin.alumni.broadcast.filters.summaryDischargedBetween", {
                      from: formatCalendarDate(filter.dischargedFrom),
                      to: formatCalendarDate(filter.dischargedTo),
                  })
            : null,
    ]
        .filter(Boolean)
        .join(", ");
